import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Tooltip } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { usePergolaConfigurationBuilderActions } from "../../../../../../store/reducers/pergola_configuration_builder";
import { useStoreDispatch, useStoreSelector } from "../../../../../../hooks/StoreHooks";

type Props = {
  lato: "Retro" | "Fronte" | "Destra" | "Sinistra";
};

type DirezioneTubolare =
  | "Retro" | "Fronte" | "Destra" | "Sinistra"
  | "Retro2" | "Fronte2" | "Destra2" | "Sinistra2";

type TubolariRowProps = {
  titolo: string;
  valore: number;
  minDisabled: boolean;
  maxDisabled: boolean;
  onChange: (valore: number) => void;
};

const stepBtn = (disabled: boolean): React.CSSProperties => ({
  width: 40,
  border: 0,
  background: "transparent",
  fontSize: 20,
  cursor: disabled ? "default" : "pointer",
  color: disabled ? "#aaa" : "inherit",
});

const TubolariRow = ({ titolo, valore, minDisabled, maxDisabled, onChange }: TubolariRowProps) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, padding: "12px 0" }}>
    <div>
      <div style={{ fontWeight: 600 }}>{titolo}</div>
      <div style={{ fontSize: "0.875rem", color: "#777" }}>
        Numero di tubolari da inserire sul {titolo.toLowerCase()} selezionato.
      </div>
    </div>
    <div style={{ display: "flex", alignItems: "stretch", background: "#f1f1f1", borderRadius: 4, overflow: "hidden", flexShrink: 0 }}>
      <button type="button" style={stepBtn(minDisabled)} disabled={minDisabled} onClick={() => onChange(valore - 1)}>−</button>
      <div style={{ width: 56, display: "flex", alignItems: "center", justifyContent: "center", background: "#fff", margin: "1px 0", fontWeight: 500 }}>
        {valore}
      </div>
      <button type="button" style={stepBtn(maxDisabled)} disabled={maxDisabled} onClick={() => onChange(valore + 1)}>+</button>
    </div>
  </div>
);

export const TubolariChoice = ({ lato }: Props) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useStoreDispatch();
  const { pergolaConfigurationBuilder } = useStoreSelector((store) => store);
  const { setStepActive, setStepComplete, setTubolareSingolo } = usePergolaConfigurationBuilderActions();

  const fioriereAttuali = pergolaConfigurationBuilder.fioriere;
  const dimensioni = pergolaConfigurationBuilder.configuration?.dimensioni;

  const isLatoLaterale = lato === "Sinistra" || lato === "Destra";
  const misuraRilevante = isLatoLaterale ? dimensioni?.profondita : dimensioni?.larghezza;
  const misura = misuraRilevante ?? 2000;
  const misuraNome = isLatoLaterale ? "profondità" : "larghezza";

  const CONFLITTI_TUBOLARI = {
    Fronte: { principale: "pilastro_H4", secondario: "pilastro_H3" },
    Retro:  { principale: "pilastro_H1", secondario: "pilastro_H2" },
    Sinistra: { principale: "pilastro_H4", secondario: "pilastro_H1" },
    Destra: { principale: "pilastro_H3", secondario: "pilastro_H2" },
  } as const;

  const conflitti = CONFLITTI_TUBOLARI[lato];

  // Conflitto con fioriere
  const haFiorieraPrincipale = fioriereAttuali.some(f => f.direzione === conflitti.principale);
  const haFiorieraSecondaria = fioriereAttuali.some(f => f.direzione === conflitti.secondario);

  // Calcolo massimo tubolari possibili: (misura - 270mm margine) / (40mm tubolare + 40mm spazio)
  const maxTubolari = Math.floor((misura - 270) / 80);

  const [numeroMain, setNumeroMain] = useState<number>(0);
  const [numeroSecond, setNumeroSecond] = useState<number>(0);
  const [touched, setTouched] = useState<{ main: boolean; second: boolean }>({
    main: false,
    second: false,
  });

  const totaleTubolari = numeroMain + numeroSecond;
  const isValid = totaleTubolari <= maxTubolari && totaleTubolari >= 0;
  const showError = (touched.main || touched.second) && !isValid;

  // Caricamento valori esistenti dalla configurazione
  useEffect(() => {
    const tubolareMain = pergolaConfigurationBuilder.tubolari.find((t) => t.direzione === lato);
    const tubolareSecond = pergolaConfigurationBuilder.tubolari.find(
      (t) => t.direzione === `${lato}2` as DirezioneTubolare
    );

    const main = tubolareMain?.numero ?? 0;
    const second = tubolareSecond?.numero ?? 0;

    setNumeroMain(main);
    setNumeroSecond(second);
    setTouched({ main: main > 0, second: second > 0 });
  }, [lato, pergolaConfigurationBuilder.tubolari]);

  // Validazione iniziale della configurazione
  useEffect(() => {
    if (!id || isNaN(Number(id)) || !pergolaConfigurationBuilder.configuration) {
      navigate("/configurations");
    } else {
      dispatch(setStepActive({ stepSlug: "estetica", isActive: true }));
    }
  }, [id, navigate, pergolaConfigurationBuilder.configuration, dispatch]);

  const handleChange = (direzione: DirezioneTubolare, value: string) => {
    const num = Math.max(0, Math.floor(parseInt(value, 10) || 0));
    // Limita automaticamente al massimo consentito
    const limitedNum = Math.min(num, maxTubolari);

    if (direzione === lato) {
      setNumeroMain(limitedNum);
      setTouched((prev) => ({ ...prev, main: true }));
    } else if (direzione === `${lato}2` as DirezioneTubolare) {
      setNumeroSecond(limitedNum);
      setTouched((prev) => ({ ...prev, second: true }));
    }

    dispatch(
      setTubolareSingolo({
        direzione,
        numero: limitedNum,
      })
    );
  };

  const submit = () => {
    if (!isValid) return;

    dispatch(setStepComplete({ stepSlug: "illuminazioni", isComplete: true }));
    navigate(`/pergola-configurations/${id}/next-step`);
  };

  // Sui lati frontale/posteriore i due gruppi stanno a sinistra e a destra; sui laterali
  // stanno verso il fronte e verso il retro (pilastri H4/H3 davanti, H1/H2 dietro).
  const titoloMain = isLatoLaterale ? "Lato frontale" : "Lato sinistro";
  const titoloSecond = isLatoLaterale ? "Lato posteriore" : "Lato destro";

  const renderRow = (titolo: string, direzione: DirezioneTubolare, valore: number) => (
    <TubolariRow
      key={titolo}
      titolo={titolo}
      valore={valore}
      minDisabled={valore <= 0}
      maxDisabled={totaleTubolari >= maxTubolari}
      onChange={(n: number) => handleChange(direzione, String(n))}
    />
  );

  return (
    <div>
      <h6 style={{ fontWeight: 500, display: "flex", alignItems: "center", gap: 8 }}>
        2. Numero tubolari
        <Tooltip title={`Massimo ${maxTubolari} tubolari per questo lato, calcolati sulla ${misuraNome} di ${misura} mm`}>
          <InfoIcon color="primary" fontSize="small" />
        </Tooltip>
      </h6>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        noValidate
      >
        {!haFiorieraPrincipale && renderRow(titoloMain, lato, numeroMain)}
        {!haFiorieraSecondaria && renderRow(titoloSecond, `${lato}2` as DirezioneTubolare, numeroSecond)}

        <div style={{ marginTop: 12, padding: "16px 20px", background: "#f1f1f1", borderRadius: 8 }}>
          <h6 style={{ fontWeight: 400, margin: 0 }}>
            Totale tubolari selezionati:{" "}
            <span style={{ color: "var(--bs-primary, #ff5a1f)", fontWeight: 700, fontSize: "1.5rem" }}>{totaleTubolari}</span>
            <span style={{ color: "#777", fontSize: "0.9rem" }}> / {maxTubolari}</span>
          </h6>
          {showError && (
            <Alert severity="error" className="mt-3">
              Il totale non può superare {maxTubolari} tubolari per questo lato. Riduci il numero inserito.
            </Alert>
          )}
        </div>
      </form>
    </div>
  );
};
